var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
export var COLUMN_PREFS_STORAGE_PREFIX = "pvColumns.";
export var MIN_COLUMN_WEIGHT = 4;
// MCL resolves percentages against (width - 30px row margin) and reserves 17px for the scrollbar
export var MCL_ROW_MARGIN = 30;
var MCL_SCROLLBAR_WIDTH = 17;
export var EMPTY_COLUMN_PREFS = {
    order: [],
    hidden: [],
    shown: [],
    widths: {},
};
export var resolveStripesUserId = function (stripesUser) {
    var _a, _b;
    var session = stripesUser;
    return (_b = (_a = session === null || session === void 0 ? void 0 : session.user) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : "anonymous";
};
export var buildColumnPrefsStorageKey = function (userId, view) { return "".concat(COLUMN_PREFS_STORAGE_PREFIX).concat(userId, ".").concat(view); };
var asStringArray = function (value) {
    return Array.isArray(value)
        ? value.filter(function (entry) { return typeof entry === "string"; })
        : [];
};
var parseColumnWeights = function (value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return {};
    }
    return Object.fromEntries(Object.entries(value).filter(function (entry) {
        return typeof entry[1] === "number" &&
            Number.isFinite(entry[1]) &&
            entry[1] > 0;
    }));
};
export var parseColumnPrefs = function (raw) {
    if (!raw) {
        return EMPTY_COLUMN_PREFS;
    }
    try {
        var parsed = JSON.parse(raw);
        return {
            order: asStringArray(parsed.order),
            hidden: asStringArray(parsed.hidden),
            shown: asStringArray(parsed.shown),
            widths: parseColumnWeights(parsed.widths),
        };
    }
    catch (_a) {
        return EMPTY_COLUMN_PREFS;
    }
};
var manageableIds = function (descriptors) {
    return descriptors.filter(function (column) { return !column.locked; }).map(function (column) { return column.id; });
};
var lockedIds = function (descriptors) {
    return descriptors.filter(function (column) { return column.locked; }).map(function (column) { return column.id; });
};
/**
 * Merges the stored order with the columns the application currently offers:
 * ids that disappeared are dropped, ids added since the preference was stored
 * land next to the neighbour they follow by default instead of at the end.
 */
export var mergeColumnOrder = function (descriptors, storedOrder) {
    var defaultOrder = manageableIds(descriptors);
    var defaultSet = new Set(defaultOrder);
    var merged = storedOrder.filter(function (id) { return defaultSet.has(id); });
    var mergedSet = new Set(merged);
    defaultOrder.forEach(function (id, defaultIndex) {
        if (mergedSet.has(id)) {
            return;
        }
        var precedingKnownId = defaultOrder
            .slice(0, defaultIndex)
            .reverse()
            .find(function (candidate) { return mergedSet.has(candidate); });
        var insertAt = precedingKnownId
            ? merged.indexOf(precedingKnownId) + 1
            : 0;
        merged.splice(insertAt, 0, id);
        mergedSet.add(id);
    });
    return merged;
};
export var resolveVisibleColumnIds = function (descriptors, prefs) {
    var hidden = new Set(prefs.hidden);
    var byId = new Map(descriptors.map(function (column) { return [column.id, column]; }));
    var shown = new Set(prefs.shown);
    var isHidden = function (id) {
        var column = byId.get(id);
        return (!(column === null || column === void 0 ? void 0 : column.alwaysVisible) &&
            ((column === null || column === void 0 ? void 0 : column.defaultHidden) ? !shown.has(id) : hidden.has(id)));
    };
    return __spreadArray(__spreadArray([], lockedIds(descriptors), true), mergeColumnOrder(descriptors, prefs.order).filter(function (id) { return !isHidden(id); }), true);
};
export var isColumnLayoutCustomized = function (descriptors, prefs) {
    var current = resolveVisibleColumnIds(descriptors, prefs);
    var defaults = resolveVisibleColumnIds(descriptors, EMPTY_COLUMN_PREFS);
    return (Object.keys(prefs.widths).length > 0 ||
        current.length !== defaults.length ||
        current.some(function (id, index) { return id !== defaults[index]; }));
};
export var toggleHiddenColumnId = function (hidden, id) {
    return hidden.includes(id)
        ? hidden.filter(function (entry) { return entry !== id; })
        : __spreadArray(__spreadArray([], hidden, true), [id], false);
};
export var toggleColumnVisibility = function (prefs, descriptor) {
    var _a;
    if (descriptor.locked || descriptor.alwaysVisible)
        return prefs;
    var field = descriptor.defaultHidden ? "shown" : "hidden";
    return __assign(__assign({}, prefs), (_a = {}, _a[field] = toggleHiddenColumnId(prefs[field], descriptor.id), _a));
};
export var moveColumnId = function (order, id, offset) {
    var from = order.indexOf(id);
    var to = from + offset;
    if (from === -1 || to < 0 || to >= order.length) {
        return order;
    }
    var moved = __spreadArray([], order, true);
    moved.splice(from, 1);
    moved.splice(to, 0, id);
    return moved;
};
export var isRelativeWidth = function (width) {
    return width === undefined || width.trim().endsWith("%");
};
// the bulk-selection column holds a single checkbox, so it is the same narrow strip on every list
export var SELECTION_COLUMN_WIDTH = "36px";
/**
 * Widths of the columns that stay outside the relative model: pixel widths and
 * every locked column. A locked column keeps its configured width and only
 * falls back to the checkbox strip when no width is configured for it.
 */
export var resolveFixedColumnWidths = function (visibleIds, defaultWidths, lockedIds) {
    var locked = new Set(lockedIds);
    var entries = visibleIds.flatMap(function (id) {
        var configured = defaultWidths === null || defaultWidths === void 0 ? void 0 : defaultWidths[id];
        if (locked.has(id)) {
            return [[id, configured !== null && configured !== void 0 ? configured : SELECTION_COLUMN_WIDTH]];
        }
        return isRelativeWidth(configured) ? [] : [[id, configured]];
    });
    return Object.fromEntries(entries);
};
export var resolveRelativeColumnIds = function (visibleIds, fixedWidths) { return visibleIds.filter(function (id) { return fixedWidths[id] === undefined; }); };
export var resolveResizeBoundaries = function (visibleIds, weights) {
    return visibleIds.flatMap(function (leftId, index) {
        var rightId = visibleIds[index + 1];
        if (rightId === undefined ||
            weights[leftId] === undefined ||
            weights[rightId] === undefined) {
            return [];
        }
        return [{ leftId: leftId, rightId: rightId }];
    });
};
var sumOfWidths = function (widths, unit) {
    return Object.values(widths !== null && widths !== void 0 ? widths : {})
        .filter(function (width) { return width.trim().endsWith(unit); })
        .reduce(function (sum, width) { return sum + parseFloat(width); }, 0);
};
// until the container is measured, keep the share the lists were tuned with so the first paint cannot overflow
var UNMEASURED_RELATIVE_BUDGET = 94;
/**
 * Share of the MCL width (in %) the relative columns may use together without
 * producing a horizontal scrollbar; the rest belongs to absolute columns, the
 * row margin and the scrollbar.
 */
export var relativeWidthBudget = function (containerWidth, fixedWidths) {
    var fixedPercent = sumOfWidths(fixedWidths, "%");
    if (!containerWidth || containerWidth <= MCL_ROW_MARGIN) {
        return Math.max(0, Math.min(UNMEASURED_RELATIVE_BUDGET, 100 - fixedPercent));
    }
    var percentBase = containerWidth - MCL_ROW_MARGIN;
    var available = percentBase - MCL_SCROLLBAR_WIDTH - sumOfWidths(fixedWidths, "px");
    var budget = Math.floor((available / percentBase) * 1000) / 10 - fixedPercent;
    return Math.min(100, Math.max(0, budget));
};
var scaleToBudget = function (weights, budget) {
    var total = Object.values(weights).reduce(function (sum, weight) { return sum + weight; }, 0);
    if (total <= 0) {
        return weights;
    }
    return Object.fromEntries(Object.entries(weights).map(function (_a) {
        var id = _a[0], weight = _a[1];
        return [
            id,
            (weight / total) * budget,
        ];
    }));
};
/**
 * Weights of the visible relative columns normalized to the budget: stored
 * weights win, then default percentages, columns without either take the
 * average share so a newly shown column never renders collapsed.
 */
export var resolveColumnWeights = function (relativeIds, defaultWidths, storedWeights, budget) {
    var ids = relativeIds;
    if (ids.length === 0) {
        return {};
    }
    var known = new Map();
    ids.forEach(function (id) {
        var stored = storedWeights[id];
        var fallback = defaultWidths === null || defaultWidths === void 0 ? void 0 : defaultWidths[id];
        if (stored !== undefined) {
            known.set(id, stored);
        }
        else if (fallback !== undefined) {
            known.set(id, parseFloat(fallback));
        }
    });
    var knownWeights = Array.from(known.values());
    var averageWeight = knownWeights.length > 0
        ? knownWeights.reduce(function (sum, weight) { return sum + weight; }, 0) /
            knownWeights.length
        : 1;
    var raw = Object.fromEntries(ids.map(function (id) { var _a; return [id, (_a = known.get(id)) !== null && _a !== void 0 ? _a : averageWeight]; }));
    return scaleToBudget(raw, budget);
};
export var mergeColumnWeights = function (stored, updated) { return (__assign(__assign({}, stored), updated)); };
var distribute = function (weights, ids, amount, shareOf) {
    var total = ids.reduce(function (sum, id) { return sum + shareOf(weights[id]); }, 0);
    if (total <= 0) {
        return weights;
    }
    var next = __assign({}, weights);
    ids.forEach(function (id) {
        next[id] = weights[id] - (amount * shareOf(weights[id])) / total;
    });
    return next;
};
/**
 * Widens (or narrows) the column left of a boundary and takes the difference
 * from every column to its right, proportionally: taking from the single
 * neighbour only would stop at its minimum after a few pixels.
 */
export var resizeColumnWeights = function (weights, orderedIds, leftId, delta) {
    var _a;
    var left = weights[leftId];
    var leftIndex = orderedIds.indexOf(leftId);
    if (left === undefined || leftIndex === -1) {
        return weights;
    }
    var rightIds = orderedIds
        .slice(leftIndex + 1)
        .filter(function (id) { return weights[id] !== undefined; });
    if (rightIds.length === 0) {
        return weights;
    }
    var roomOnTheRight = rightIds.reduce(function (sum, id) { return sum + Math.max(0, weights[id] - MIN_COLUMN_WEIGHT); }, 0);
    var roomOnTheLeft = Math.max(0, left - MIN_COLUMN_WEIGHT);
    var applied = Math.min(Math.max(delta, -roomOnTheLeft), roomOnTheRight);
    if (applied === 0) {
        return weights;
    }
    var shareOf = applied > 0
        ? function (weight) { return Math.max(0, weight - MIN_COLUMN_WEIGHT); }
        : function (weight) { return weight; };
    var next = distribute(weights, rightIds, applied, shareOf);
    return __assign(__assign({}, next), (_a = {}, _a[leftId] = left + applied, _a));
};
export var composeColumnWidths = function (visibleIds, fixedWidths, weights) {
    var absolute = Object.entries(fixedWidths).filter(function (_a) {
        var id = _a[0];
        return visibleIds.includes(id);
    });
    var relative = Object.entries(weights).map(function (_a) {
        var id = _a[0], weight = _a[1];
        return [
            id,
            "".concat(Math.round(weight * 100) / 100, "%"),
        ];
    });
    var entries = __spreadArray(__spreadArray([], absolute, true), relative, true);
    return entries.length > 0 ? Object.fromEntries(entries) : undefined;
};
export var resolveColumnWidths = function (visibleIds, defaultWidths, storedWeights, budget, lockedIds) {
    if (lockedIds === void 0) { lockedIds = []; }
    if (!defaultWidths && Object.keys(storedWeights).length === 0) {
        return undefined;
    }
    var fixedWidths = resolveFixedColumnWidths(visibleIds, defaultWidths, lockedIds);
    return composeColumnWidths(visibleIds, fixedWidths, resolveColumnWeights(resolveRelativeColumnIds(visibleIds, fixedWidths), defaultWidths, storedWeights, budget));
};
